'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { InstallationItem } from '../molecular/InstallationItem';
import { InfoTooltip } from '../atomic/Tooltip';

export interface Installation {
  readonly id: string;
  readonly name: string;
  readonly type: 'solar' | 'water' | 'biomass' | 'biogas';
  readonly capacity: number; // in kW or kWp
  readonly yearlyProduction: number; // in kWh
}

interface InstallationsExplorerProps {
  installations: Installation[];
  className?: string;
}

// Named export for better tree-shaking and explicit imports
export function InstallationsExplorer({ 
  installations, 
  className = '' 
}: InstallationsExplorerProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 3;
  
  // Sort by yearly production (highest first)
  const sortedInstallations = [...installations].sort((a, b) => 
    b.yearlyProduction - a.yearlyProduction
  );
  
  // Calculate pagination
  const totalPages = Math.ceil(sortedInstallations.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const currentInstallations = sortedInstallations.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  
  // Calculate total production
  const totalProduction = installations.reduce(
    (sum, inst) => sum + inst.yearlyProduction, 
    0
  );

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">
          TOP ENERGIEERZEUGER:
          <InfoTooltip 
            content={{
              title: "Größte Energieerzeuger",
              description: "Die produktivsten erneuerbaren Energieanlagen in Ihrer Gemeinde, sortiert nach Jahresproduktion."
            }}
          />
        </h2>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, sortedInstallations.length)} von {sortedInstallations.length}
        </div>
      </div>
      
      <div className="space-y-2 min-h-[200px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-2"
          >
            {currentInstallations.map((installation, index) => (
              <motion.div
                key={installation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.4, 
                  delay: index * 0.1,
                  type: "spring",
                  stiffness: 120,
                  damping: 20
                }}
              >
                <InstallationItem
                  name={installation.name}
                  type={installation.type}
                  capacity={installation.capacity}
                  yearlyProduction={installation.yearlyProduction}
                  isLast={index === currentInstallations.length - 1}
                />
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
      
      {/* Navigation buttons */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <motion.button
            onClick={handlePrevious}
            disabled={currentPage === 0}
            className={`flex items-center text-sm px-3 py-2 rounded-md transition-colors ${
              currentPage === 0 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            }`}
            whileHover={currentPage > 0 ? { scale: 1.02 } : undefined}
            whileTap={currentPage > 0 ? { scale: 0.98 } : undefined}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Vorherige 3
          </motion.button>
          
          <motion.button
            onClick={handleNext}
            disabled={currentPage === totalPages - 1}
            className={`flex items-center text-sm px-3 py-2 rounded-md transition-colors ${
              currentPage === totalPages - 1 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            }`}
            whileHover={currentPage < totalPages - 1 ? { scale: 1.02 } : undefined}
            whileTap={currentPage < totalPages - 1 ? { scale: 0.98 } : undefined}
          >
            Nächste 3
            <ChevronRight className="h-4 w-4 ml-1" />
          </motion.button>
        </div>
      )}
      
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Gesamte Jahresproduktion (2024):
            <InfoTooltip 
              content="Die Summe der jährlichen Energieproduktion aller erneuerbaren Energieanlagen in Ihrer Gemeinde."
            />
          </span>
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {(totalProduction / 1000000).toLocaleString('de-AT', { 
              maximumFractionDigits: 1 
            })} GWh
          </span>
        </div>
      </div>
    </div>
  );
}
